import { Link } from 'react-router';
import { ChevronDown, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DetailPageHeader } from '@/components/common/DetailPageHeader';
import { StatusBadge } from '@/components/common/StatusBadge';
import { useJobCostReport } from '../hooks/useJobCostReport';
import type { JobDetailDto, JobStatus } from '../types';

interface JobHeaderProps {
  job: JobDetailDto;
  onStatusChange: (newStatus: JobStatus) => void;
  onEditClick: () => void;
}

export function JobHeader({ job, onStatusChange, onEditClick }: JobHeaderProps) {
  const { downloadPdf, downloadExcel, openPdf, isLoading: reportLoading } = useJobCostReport(job.id);
  const getStatusVariant = (status: JobStatus) => {
    switch (status) {
      case 'Sold':
        return 'active' as const;
      case 'NoGo':
        return 'error' as const;
      case 'Closed':
        return 'purple' as const;
      default:
        return 'inactive' as const;
    }
  };

  const getValidTransitions = (currentStatus: JobStatus): JobStatus[] => {
    switch (currentStatus) {
      case 'Sold':
        return ['Closed', 'NoGo'];
      case 'Closed':
        return ['Sold'];
      case 'NoGo':
        return ['Sold'];
      default:
        return [];
    }
  };

  const validTransitions = getValidTransitions(job.status);

  return (
    <DetailPageHeader
      left={
        <>
          <h1 className="text-2xl font-bold text-secondary">{job.jobNumber}</h1>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <StatusBadge
                variant={getStatusVariant(job.status)}
                className="cursor-pointer text-sm flex items-center gap-1"
                aria-label={`Change status. Current status: ${job.status}`}
              >
                {job.status}
                <ChevronDown className="h-3 w-3" />
              </StatusBadge>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-white">
              {validTransitions.map((status) => (
                <DropdownMenuItem
                  key={status}
                  onClick={() => onStatusChange(status)}
                  className="cursor-pointer"
                >
                  Change to {status}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      }
      middle={
        <Link
          to={`/leads/${job.leadId}`}
          className="text-lg font-semibold text-secondary hover:underline"
        >
          {job.lead.customerName}
        </Link>
      }
      right={
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="rounded-md"
                disabled={reportLoading}
              >
                <FileText className="h-4 w-4 mr-1" />
                {reportLoading ? 'Loading...' : 'Report'}
                <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              <DropdownMenuItem onClick={openPdf} className="cursor-pointer">
                View PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={downloadPdf} className="cursor-pointer">
                Download PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={downloadExcel} className="cursor-pointer">
                Download Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            onClick={onEditClick}
            variant="outline"
            size="sm"
            className="rounded-md"
          >
            Edit
          </Button>
        </div>
      }
    />
  );
}
